import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TextField, Chip, Box, Typography, CircularProgress } from '@mui/material';

type KeywordSuggestion = {
  keyword?: string;
  source?: string;
  confidence?: number;
  weight?: number;
  matchCount?: number;
  patternId?: number;
};

type Props = {
  companyId: number;
  isSoftware: boolean;
  onKeywordSelected: (keyword: string, source: string, confidence: number) => void;
  suggestedKeywords?: {
    software?: KeywordSuggestion[];
    nonSoftware?: KeywordSuggestion[];
  } | null;
  loading?: boolean;
};

const ValidationReasonSelect = ({
  companyId,
  isSoftware,
  onKeywordSelected,
  suggestedKeywords,
  loading = false,
}: Props) => {
  const { t } = useTranslation();
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordSuggestion | null>(null);
  const [customKeyword, setCustomKeyword] = useState<string>('');

  // Determine which keywords to show based on classification
  const keywords = isSoftware ? suggestedKeywords?.software || [] : suggestedKeywords?.nonSoftware || [];

  // Handle keyword selection
  const handleKeywordChange = (event: React.SyntheticEvent, value: KeywordSuggestion | string | null) => {
    if (typeof value === 'string') {
      // Custom keyword entered
      setCustomKeyword(value);
      setSelectedKeyword(null);
      onKeywordSelected(value, 'custom', 0.5);
    } else if (value && value.keyword) {
      // Suggested keyword selected
      setSelectedKeyword(value);
      setCustomKeyword('');
      onKeywordSelected(value.keyword, value.source || 'unknown', value.confidence || 0.5);
    }
  };

  // Format option label with metadata
  const getOptionLabel = (option: KeywordSuggestion | string): string => {
    if (typeof option === 'string') {
      return option;
    }
    return option.keyword || '';
  };

  // Render option with confidence chip
  const renderOption = (props: any, option: KeywordSuggestion | string) => {
    if (typeof option === 'string') {
      return (
        <li {...props}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="body2">{option}</Typography>
            <Chip label="Custom" size="small" color="default" sx={{ ml: 'auto' }} />
          </Box>
        </li>
      );
    }

    const confidencePercent = Math.round((option.confidence || 0.5) * 100);
    const confidenceColor = confidencePercent >= 80 ? 'success' : confidencePercent >= 60 ? 'warning' : 'default';

    return (
      <li {...props}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <Typography variant="body2">{option.keyword || ''}</Typography>
          <Chip label={`${confidencePercent}%`} size="small" color={confidenceColor} sx={{ ml: 'auto' }} />
        </Box>
      </li>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          {t('assessment.loadingKeywords')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 500 }}>
      <Autocomplete
        freeSolo
        options={keywords}
        value={selectedKeyword}
        onChange={handleKeywordChange}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={params => (
          <TextField
            {...params}
            label={isSoftware ? t('assessment.whySoftware') : t('assessment.whyNotSoftware')}
            placeholder={t('assessment.selectOrTypeKeyword')}
            variant="outlined"
            size="small"
          />
        )}
        noOptionsText={t('assessment.noSuggestedKeywords')}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
          },
        }}
      />
      {selectedKeyword && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {t('assessment.source')}: {selectedKeyword.source || 'unknown'} • {t('assessment.confidence')}:{' '}
            {Math.round((selectedKeyword.confidence || 0.5) * 100)}%
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ValidationReasonSelect;
